import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, CheckCheck } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { SERVICES } from "@/lib/siteContent";

const serviceName = (slug) => SERVICES.find((s) => s.slug === slug)?.title || "Informazioni generali";

const Item = ({ r, onRead, onDelete }) => (
  <article data-testid={`richiesta-${r.id}`} className={`rounded-xl border bg-white p-5 ${r.letto ? "border-slate-200" : "border-blue-300 shadow-[0_0_0_3px_rgba(37,99,235,0.08)]"}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          {!r.letto && <span className="h-2 w-2 rounded-full bg-blue-600" />}
          <p className="font-heading font-semibold text-slate-900">{r.nome}</p>
          {r.organizzazione && <span className="text-sm text-slate-500">· {r.organizzazione}</span>}
        </div>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-blue-600">{serviceName(r.servizio)}</p>
      </div>
      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString("it-IT")}</span>
    </div>
    <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700">{r.messaggio}</p>
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
      <div className="flex flex-wrap gap-4 text-sm">
        <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-blue-600"><Mail className="h-3.5 w-3.5" /> {r.email}</a>
        {r.telefono && <a href={`tel:${r.telefono}`} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-blue-600"><Phone className="h-3.5 w-3.5" /> {r.telefono}</a>}
      </div>
      <div className="flex gap-1">
        <button onClick={() => onRead(r)} data-testid={`richiesta-toggle-read-${r.id}`} className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"><CheckCheck className="h-3.5 w-3.5" /> {r.letto ? "Segna da leggere" : "Segna come letta"}</button>
        <button onClick={() => onDelete(r)} data-testid={`richiesta-delete-${r.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </article>
);

export default function Richieste() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => http.get("/admin/contatti").then(({ data }) => setItems(data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const onRead = async (r) => {
    try {
      await http.patch(`/admin/contatti/${r.id}`, { letto: !r.letto });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Eliminare la richiesta di ${r.nome}?`)) return;
    try {
      await http.delete(`/admin/contatti/${r.id}`);
      toast.success("Richiesta eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!items) return null;
  return (
    <div className="fade-up space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Sito</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-slate-900">Richieste di contatto</h1>
        <p className="mt-1 text-sm text-slate-500">{items.filter((r) => !r.letto).length} da leggere su {items.length}</p>
      </div>
      <div className="space-y-4" data-testid="richieste-list">
        {items.map((r) => <Item key={r.id} r={r} onRead={onRead} onDelete={onDelete} />)}
        {items.length === 0 && <p data-testid="richieste-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">Nessuna richiesta ricevuta.</p>}
      </div>
    </div>
  );
}
